import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { consolidadoAPI } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { toast } from 'sonner';
import {
  Building2, TrendingUp, TrendingDown, Wallet, Percent, Layers,
  Check, Trophy, Info, BarChart2,
} from 'lucide-react';

const MONTH_NAMES = ['', 'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const MONTH_LABELS = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const SummaryCard = ({ title, value, icon: Icon, isPercent = false, tone = 'default' }) => {
  const toneColor = tone === 'positive' ? '#22C55E' : tone === 'negative' ? '#EF4444' : '#e4e4e7';
  return (
    <div className="rounded-xl bg-zinc-900 border border-white/10 p-5" data-testid={`consolidado-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-wider text-zinc-500">{title}</p>
        <div className="p-2 rounded-lg bg-[#22C55E]/10">
          <Icon className="text-[#22C55E]" size={18} />
        </div>
      </div>
      <p className="text-2xl font-bold" style={{ color: toneColor }}>
        {isPercent ? `${(value || 0).toFixed(1)}%` : formatCurrency(value || 0)}
      </p>
    </div>
  );
};

export const ConsolidadoPage = () => {
  const { gyms } = useGym();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [modo, setModo] = useState(() => localStorage.getItem('geral_modo_visao') || 'operacional');
  const [selectedIds, setSelectedIds] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Inicializa seleção: usa a salva (filtrada pelas unidades existentes) ou todas
  useEffect(() => {
    if (!gyms || gyms.length === 0) return;
    const allIds = gyms.map((g) => g.id);
    let saved = [];
    try {
      saved = JSON.parse(localStorage.getItem('geral_selected_gyms') || '[]');
    } catch (e) {
      saved = [];
    }
    const valid = saved.filter((id) => allIds.includes(id));
    setSelectedIds(valid.length > 0 ? valid : allIds);
  }, [gyms]);

  const toggleUnit = (id) => {
    setSelectedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem('geral_selected_gyms', JSON.stringify(next));
      return next;
    });
  };

  const selectAll = () => {
    const allIds = gyms.map((g) => g.id);
    setSelectedIds(allIds);
    localStorage.setItem('geral_selected_gyms', JSON.stringify(allIds));
  };

  const handleModo = (m) => {
    setModo(m);
    localStorage.setItem('geral_modo_visao', m);
  };

  const loadData = useCallback(async () => {
    if (!selectedIds || selectedIds.length === 0) {
      setData(null);
      return;
    }
    try {
      setLoading(true);
      const res = await consolidadoAPI.getOverview(selectedIds, MONTH_NAMES[selectedMonth], selectedYear, modo);
      setData(res.data);
    } catch (e) {
      toast.error('Erro ao carregar o consolidado');
    } finally {
      setLoading(false);
    }
  }, [selectedIds, selectedMonth, selectedYear, modo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const maxFat = data && data.por_unidade.length > 0
    ? Math.max(...data.por_unidade.map((u) => u.faturamento), 1)
    : 1;

  const revLabel = modo === 'geral' ? 'Recebido (Total)' : 'Faturamento (Total)';
  const expLabel = modo === 'geral' ? 'Saídas (Total)' : 'Despesas (Total)';
  const profitLabel = modo === 'geral' ? 'Lucro Líquido' : 'Resultado Operacional';
  const marginLabel = modo === 'geral' ? 'Margem Líquida' : 'Margem Operacional';

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="consolidado-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-[#22C55E]/10">
                <Layers className="text-[#22C55E]" size={26} />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Visão Geral (Consolidado)</h1>
            </div>
            <p className="text-zinc-400 mt-2 text-sm">
              Resumo financeiro somado das unidades selecionadas. Apenas informativo.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center rounded-lg bg-zinc-800 border border-zinc-700 p-0.5" data-testid="consolidado-modo-toggle" title="Operacional = só a operação (igual DRE). Geral = tudo que entrou e saiu do banco (visão do presidente).">
              <button
                onClick={() => handleModo('operacional')}
                className={`px-3 h-9 rounded-md text-sm transition-colors ${modo === 'operacional' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                data-testid="consolidado-modo-operacional"
              >
                Operacional
              </button>
              <button
                onClick={() => handleModo('geral')}
                className={`px-3 h-9 rounded-md text-sm transition-colors ${modo === 'geral' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                data-testid="consolidado-modo-geral"
              >
                Geral
              </button>
            </div>

            <select
              data-testid="consolidado-month-selector"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="h-9 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm"
            >
              {MONTH_LABELS.slice(1).map((label, i) => (
                <option key={i + 1} value={i + 1}>{label}</option>
              ))}
            </select>
            <select
              data-testid="consolidado-year-selector"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="h-9 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm"
            >
              {[selectedYear - 2, selectedYear - 1, selectedYear, selectedYear + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Seletor de unidades */}
        <div className="rounded-xl bg-zinc-900 border border-white/10 p-5" data-testid="consolidado-unit-selector">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Building2 className="text-[#22C55E]" size={18} />
              <p className="text-sm font-semibold text-white">
                Unidades no consolidado
                <span className="text-zinc-500 font-normal ml-2">
                  ({selectedIds.length} de {gyms.length})
                </span>
              </p>
            </div>
            <button
              onClick={selectAll}
              className="text-xs text-[#22C55E] hover:underline"
              data-testid="consolidado-select-all"
            >
              Selecionar todas
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {gyms.map((g) => {
              const active = selectedIds.includes(g.id);
              return (
                <button
                  key={g.id}
                  onClick={() => toggleUnit(g.id)}
                  data-testid={`consolidado-unit-${g.id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-all ${
                    active
                      ? 'bg-[#22C55E]/10 border-[#22C55E]/40 text-white'
                      : 'bg-zinc-800/50 border-white/10 text-zinc-400 hover:text-white'
                  }`}
                >
                  <span className={`w-4 h-4 rounded flex items-center justify-center ${active ? 'bg-[#22C55E]' : 'border border-zinc-600'}`}>
                    {active && <Check size={12} className="text-black" />}
                  </span>
                  {g.name}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-zinc-500">Carregando consolidado...</div>
        ) : !data || selectedIds.length === 0 ? (
          <div className="text-center py-16 text-zinc-500" data-testid="consolidado-empty">
            Selecione ao menos uma unidade para ver o consolidado.
          </div>
        ) : (
          <>
            {/* Cards resumo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="consolidado-summary">
              <SummaryCard title={revLabel} value={data.total_revenue} icon={TrendingUp} />
              <SummaryCard title={expLabel} value={data.total_expenses} icon={TrendingDown} />
              <SummaryCard title={profitLabel} value={data.profit} icon={Wallet} tone={data.profit >= 0 ? 'positive' : 'negative'} />
              <SummaryCard title={marginLabel} value={data.profit_margin} icon={Percent} isPercent tone={data.profit >= 0 ? 'positive' : 'negative'} />
              <SummaryCard title="Saldo Consolidado" value={data.saldo_consolidado} icon={Wallet} tone={data.saldo_consolidado >= 0 ? 'positive' : 'negative'} />
            </div>

            {/* Ranking por unidade */}
            <div className="rounded-xl bg-zinc-900 border border-white/10 p-5" data-testid="consolidado-ranking">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="text-[#22C55E]" size={18} />
                <h2 className="text-lg font-semibold text-white">Ranking por unidade</h2>
                <span className="text-xs text-zinc-500">(por {modo === 'geral' ? 'recebimento' : 'faturamento'})</span>
              </div>
              <div className="space-y-4">
                {data.por_unidade.map((u, idx) => (
                  <div key={u.gym_id} data-testid={`consolidado-rank-${u.gym_id}`}>
                    <div className="flex items-center justify-between mb-1.5 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === 0 ? 'bg-[#22C55E] text-black' : 'bg-zinc-800 text-zinc-400'}`}>
                          {idx + 1}
                        </span>
                        <span className="text-white font-medium truncate">{u.nome}</span>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <span className="text-white font-semibold">{formatCurrency(u.faturamento)}</span>
                        <span className={`text-xs ${u.lucro >= 0 ? 'text-[#22C55E]' : 'text-red-400'}`}>
                          {u.lucro >= 0 ? 'Lucro' : 'Prejuízo'}: {formatCurrency(u.lucro)}
                        </span>
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#22C55E] transition-all"
                        style={{ width: `${Math.max((u.faturamento / maxFat) * 100, 2)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabela comparativa unidade a unidade */}
            <div className="rounded-xl bg-zinc-900 border border-white/10 p-5" data-testid="consolidado-comparativo">
              <div className="flex items-center gap-2 mb-4">
                <BarChart2 className="text-[#22C55E]" size={18} />
                <h2 className="text-lg font-semibold text-white">Comparar unidades</h2>
              </div>
              <div className="overflow-x-auto -mx-1 px-1">
                <table className="w-full text-sm min-w-[560px]">
                  <thead>
                    <tr className="text-zinc-500 border-b border-white/10">
                      <th className="text-left font-medium py-2.5 pr-3">Unidade</th>
                      <th className="text-right font-medium py-2.5 px-3">{modo === 'geral' ? 'Recebido' : 'Faturamento'}</th>
                      <th className="text-right font-medium py-2.5 px-3">{modo === 'geral' ? 'Saídas' : 'Despesas'}</th>
                      <th className="text-right font-medium py-2.5 px-3">{modo === 'geral' ? 'Lucro Líq.' : 'Resultado'}</th>
                      <th className="text-right font-medium py-2.5 px-3">Margem</th>
                      <th className="text-right font-medium py-2.5 pl-3">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.por_unidade.map((u) => (
                      <tr key={u.gym_id} className="border-b border-white/5" data-testid={`consolidado-comp-${u.gym_id}`}>
                        <td className="py-2.5 pr-3 text-white font-medium">{u.nome}</td>
                        <td className="py-2.5 px-3 text-right text-zinc-300">{formatCurrency(u.faturamento)}</td>
                        <td className="py-2.5 px-3 text-right text-zinc-300">{formatCurrency(u.despesas)}</td>
                        <td className={`py-2.5 px-3 text-right font-semibold ${u.lucro >= 0 ? 'text-[#22C55E]' : 'text-red-400'}`}>{formatCurrency(u.lucro)}</td>
                        <td className={`py-2.5 px-3 text-right ${u.lucro >= 0 ? 'text-[#22C55E]' : 'text-red-400'}`}>{(u.margem || 0).toFixed(1)}%</td>
                        <td className={`py-2.5 pl-3 text-right ${u.saldo >= 0 ? 'text-zinc-300' : 'text-red-400'}`}>{formatCurrency(u.saldo)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="text-white font-bold" data-testid="consolidado-comp-total">
                      <td className="py-3 pr-3">Total</td>
                      <td className="py-3 px-3 text-right">{formatCurrency(data.total_revenue)}</td>
                      <td className="py-3 px-3 text-right">{formatCurrency(data.total_expenses)}</td>
                      <td className={`py-3 px-3 text-right ${data.profit >= 0 ? 'text-[#22C55E]' : 'text-red-400'}`}>{formatCurrency(data.profit)}</td>
                      <td className={`py-3 px-3 text-right ${data.profit >= 0 ? 'text-[#22C55E]' : 'text-red-400'}`}>{(data.profit_margin || 0).toFixed(1)}%</td>
                      <td className={`py-3 pl-3 text-right ${data.saldo_consolidado >= 0 ? 'text-white' : 'text-red-400'}`}>{formatCurrency(data.saldo_consolidado)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-zinc-500 rounded-lg bg-zinc-900/60 border border-white/5 p-3" data-testid="consolidado-info">
              <Info size={14} className="mt-0.5 flex-shrink-0" />
              <span>
                Esta é uma leitura consolidada apenas informativa: os valores são a soma das unidades selecionadas no mês {MONTH_LABELS[selectedMonth]}/{selectedYear}.
                As demais abas continuam analisando uma unidade por vez.
              </span>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ConsolidadoPage;
